import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useActiveCareerFocus } from '../../hooks/useCareerFocus';
import { useContentLinks } from '../../hooks/useContentLinks';
import { useRubric } from '../../hooks/useRubric';
import { useGoals } from '../../hooks/useGoals';
import { usePerformanceReview } from '../../hooks/usePerformanceReview';
import { useToast } from '../../context/ToastContext';
import { useAuthState } from '../../hooks/useAuthState';
import { supabase } from '../../utils/supabase';
import { getDateTags } from '../../utils/dateTags';
import { XMarkIcon, SparklesIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Slider, useMediaQuery } from '@mui/material';
import { IMPACT_LEVELS, ENGAGEMENT_LEVELS } from '../../constants/impactLevels';
import { useCareerCards } from '../../hooks/useCareerCards';
import { useNotes } from '../../hooks/useNotes';

interface QuickAddContext {
  title?: string;
  placeholder?: string;
  defaultImpact?: number;
  defaultEngagement?: number;
  suggestedGoals?: number[];
  suggestedRubricCategories?: string[];
  rubricItemId?: string;
  rubricCategory?: string;
  rubricDescription?: string;
}

interface EnhancedQuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: QuickAddContext;
  onSuccess?: () => void;
}

interface LinkedItem {
  id: string;
  name: string;
  type: 'goal' | 'rubric' | 'competency';
  mappedCount?: number;
  weight?: number;
}

const EnhancedQuickAddModal: React.FC<EnhancedQuickAddModalProps> = ({
  isOpen,
  onClose,
  context = {},
  onSuccess
}) => {
  const { darkMode } = useDarkMode();
  const { focus } = useActiveCareerFocus();
  const { activeRubric } = useRubric(focus?.id);
  const { goals } = useGoals(focus?.id);
  const { createBulkLinks } = useContentLinks();
  const { showToast } = useToast();
  const { user } = useAuthState();
  const isMobile = useMediaQuery('(max-width: 640px)');
  
  const isPerformanceReview = focus?.focus_type === 'performance_review';
  
  // Only fetch performance review data if it's a performance review focus
  const { competencies } = usePerformanceReview(isPerformanceReview ? focus?.id : null);
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [careerImpact, setCareerImpact] = useState(context.defaultImpact || 3);
  const [engagementLevel, setEngagementLevel] = useState(context.defaultEngagement || 3);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTabMode, setActiveTabMode] = useState<'create' | 'link'>('create');
  const [selectedExistingItems, setSelectedExistingItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  
  const titleRef = useRef<HTMLInputElement>(null);

  // Build unified list of items to link based on current focus context
  const linkedItems: LinkedItem[] = [];
  
  // Always show goals if they exist (for all focus types)
  if (goals && goals.length > 0) {
    goals.forEach((goal) => {
      // Count how many rubric items are mapped to this goal
      const mappedCount = goal.rubric_mappings?.length || 0;
      
      linkedItems.push({
        id: goal.id,
        name: goal.goal_text,
        type: 'goal',
        mappedCount
      });
    });
  }
  
  // For performance review focus, also show competencies
  if (isPerformanceReview && competencies && competencies.length > 0) {
    competencies.forEach((competency) => {
      linkedItems.push({
        id: competency.id,
        name: competency.name,
        type: 'competency',
        weight: competency.weight
      });
    });
  }
  
  // For non-performance review focus, show rubric criteria
  if (!isPerformanceReview && activeRubric && activeRubric.categories) {
    activeRubric.categories.forEach((category) => {
      // Use criteria_items if available (new format)
      if (category.criteria_items && category.criteria_items.length > 0) {
        category.criteria_items.forEach((criterion) => {
          linkedItems.push({
            id: criterion.id,
            name: `[${category.name}] ${criterion.text || criterion.description || ''}`,
            type: 'rubric'
          });
        });
      } else if (category.criteria && category.criteria.length > 0) {
        // Fallback for old format
        category.criteria.forEach((text, index) => {
          linkedItems.push({
            id: `${category.id}-${index}`,
            name: `[${category.name}] ${text}`,
            type: 'rubric'
          });
        });
      }
    });
  }
  
  // Debug logging
  // useEffect(() => {
  //   console.log('QuickAddModal Debug:', {
  //     focusId: focus?.id,
  //     focusType: focus?.focus_type,
  //     isPerformanceReview,
  //     competencies: competencies,
  //     competenciesLength: competencies?.length,
  //     goalsLength: goals?.length,
  //     linkedItemsLength: linkedItems.length
  //   });
  // }, [focus, isPerformanceReview, competencies, goals, linkedItems.length]);


  useEffect(() => {
    if (isOpen && titleRef.current) {
      titleRef.current.focus();
    }
  }, [isOpen]);

  // Pre-select suggested items
  useEffect(() => {
    if (context.suggestedGoals || context.suggestedRubricCategories || context.rubricItemId) {
      const newSelected = new Set<string>();
      
      // Handle suggested goals - these might be indices or IDs
      context.suggestedGoals?.forEach(goalIndexOrId => {
        // If goals are loaded, try to match by index or ID
        if (goals && goals.length > 0) {
          if (typeof goalIndexOrId === 'number' && goals[goalIndexOrId]) {
            newSelected.add(`goal-${goals[goalIndexOrId].id}`);
          } else {
            // Try to find by ID
            const goal = goals.find(g => g.id === goalIndexOrId);
            if (goal) {
              newSelected.add(`goal-${goal.id}`);
            }
          }
        }
      });
      
      context.suggestedRubricCategories?.forEach(categoryId => {
        // Find all criteria in this category
        const category = activeRubric?.categories.find(cat => cat.id === categoryId);
        if (category) {
          category.criteria.forEach((criterion, index) => {
            newSelected.add(`rubric-${categoryId}-${index}`);
          });
        }
      });
      
      // Handle specific rubric item
      if (context.rubricItemId) {
        // Simply add the rubric item with its ID
        newSelected.add(`rubric-${context.rubricItemId}`);
      }
      
      setSelectedItems(newSelected);
    }
  }, [context, activeRubric, goals]);

  const handleSubmit = async () => {
    // Handle link mode
    if (activeTabMode === 'link') {
      if (selectedExistingItems.size === 0 || !focus || !user || !context?.rubricItemId) return;
      
      setIsSubmitting(true);
      try {
        // Create links for selected items
        const links = Array.from(selectedExistingItems).map(itemKey => {
          // Handle the format: "type-id" where id might contain hyphens (UUID)
          const separatorIndex = itemKey.indexOf('-');
          const type = itemKey.substring(0, separatorIndex);
          const id = itemKey.substring(separatorIndex + 1);
          return {
            sourceType: type as 'career_card' | 'note',
            sourceId: id,
            targetType: 'rubric_criterion' as const,
            targetId: context.rubricItemId!,
            metadata: {}
          };
        });
        
        await createBulkLinks(links);
        
        showToast(
          `✓ ${selectedExistingItems.size} item${selectedExistingItems.size !== 1 ? 's' : ''} linked to evidence!`,
          'success'
        );
        
        // Reset and close
        setSelectedExistingItems(new Set());
        setSearchQuery('');
        onClose();
        
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        
        return;
      } catch (error) {
        console.error('Failed to link items:', error);
        showToast('Failed to link evidence', 'error');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    // Handle create mode (existing logic)
    if (!title.trim() || !focus || !user) return;

    setIsSubmitting(true);
    try {
      const createdDate = new Date().toISOString();
      const dateTags = getDateTags(createdDate, createdDate, 'note');
      
      // Create a note
      const { data: note, error } = await supabase
        .from('notes')
        .insert({
          title: title.trim(),
          content: content.trim() || '',
          custom_tags: context.rubricItemId ? ['quick-note', 'review-prep'] : ['quick-note'],
          date_tags: dateTags,
          note_date: createdDate,
          career_impact: careerImpact,
          engagement_level: engagementLevel,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;
      
      if (note) {
        // Create links
        const links = [];
        
        selectedItems.forEach(itemKey => {
          // Handle the format: "type-id" where id might contain hyphens (UUID)
          const separatorIndex = itemKey.indexOf('-');
          const type = itemKey.substring(0, separatorIndex);
          const id = itemKey.substring(separatorIndex + 1);
          
          console.log('Processing item:', { itemKey, type, id });
          
          if (type === 'goal') {
            links.push({
              sourceType: 'note',
              sourceId: note.id,
              targetType: 'goal',
              targetId: id,
              metadata: {}
            });
          } else if (type === 'competency') {
            // For performance review, we need to save to competency_evidence table
            // This will be handled after the content_links
            return; // Skip to next iteration in forEach
          } else if (type === 'rubric' && activeRubric) {
            // Check if this is a new criterion ID (UUID) or old format
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
            
            console.log('Is UUID?', isUuid, 'for id:', id);
            
            if (isUuid) {
              // New normalized structure - id is the criterion ID
              const criterion = activeRubric.categories
                .flatMap(cat => cat.criteria_items || [])
                .find(c => c.id === id);
                
              if (criterion) {
                links.push({
                  sourceType: 'note',
                  sourceId: note.id,
                  targetType: 'rubric_criterion',
                  targetId: criterion.id,
                  metadata: { 
                    category_id: criterion.category_id
                  }
                });
              }
            } else {
              // Legacy format: {category_id}-{index}
              // We need to map the category name to the actual category ID
              const parts = id.split('-');
              const itemIndex = parts[parts.length - 1];
              const categoryPart = parts.slice(0, -1).join('-');
              
              // Find the actual category
              const category = activeRubric.categories.find(cat => {
                const catIdHyphen = cat.name.toLowerCase().replace(/\s+/g, '-');
                return categoryPart === catIdHyphen;
              });
              
              if (category && itemIndex !== undefined) {
                // Use the actual category ID, not the snake_case name
                const categoryId = category.id || category.name.toLowerCase().replace(/\s+/g, '_');
                
                links.push({
                  sourceType: 'note',
                  sourceId: note.id,
                  targetType: 'rubric_item',
                  targetId: `${focus.id}_${categoryId}_${itemIndex}`,
                  metadata: { 
                    category_id: categoryId,
                    item_index: parseInt(itemIndex)
                  }
                });
              }
            }
          }
        });
        
        if (links.length > 0) {
          await createBulkLinks(links);
        }
        
        // Handle competency evidence for performance review
        if (isPerformanceReview && focus) {
          const competencyLinks = [];
          selectedItems.forEach(itemKey => {
            const separatorIndex = itemKey.indexOf('-');
            const type = itemKey.substring(0, separatorIndex);
            const id = itemKey.substring(separatorIndex + 1);
            
            if (type === 'competency') {
              competencyLinks.push({
                competency_id: id,
                evidence_id: note.id,
                evidence_type: 'note' as const,
                focus_id: focus.id,
                user_id: user.id
              });
            }
          });
          
          if (competencyLinks.length > 0) {
            const { error: compError } = await supabase
              .from('competency_evidence')
              .insert(competencyLinks);
              
            if (compError) {
              console.error('Failed to create competency evidence:', compError);
              throw compError;
            }
          }
        }
        
        showToast(
          context?.rubricItemId 
            ? '✓ Evidence captured! Great job documenting your skills.' 
            : 'Note added successfully!', 
          'success'
        );
        
        // Reset form
        setTitle('');
        setContent('');
        setCareerImpact(3);
        setEngagementLevel(3);
        setSelectedItems(new Set());
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }
        
        onClose();
      }
    } catch (error) {
      console.error('Failed to create note:', error);
      showToast('Failed to add evidence', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleItem = (itemKey: string) => {
    const newItems = new Set(selectedItems);
    if (newItems.has(itemKey)) {
      newItems.delete(itemKey);
    } else {
      newItems.add(itemKey);
    }
    setSelectedItems(newItems);
  };

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-200' : 'text-gray-800';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';

  // Color constants for sliders
  const CAREER_IMPACT_COLORS = {
    primary: '#FFA726',
    hover: 'rgba(255, 167, 38, 0.16)',
    active: 'rgba(255, 167, 38, 0.16)'
  };

  const ENGAGEMENT_COLORS = {
    primary: '#26A69A',
    hover: 'rgba(38, 166, 154, 0.16)',
    active: 'rgba(38, 166, 154, 0.16)'
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className={`mx-auto max-w-2xl w-full ${bgColor} rounded-xl shadow-xl my-8 max-h-[90vh] flex flex-col`}>
          {/* Header - Fixed */}
          <div className={`px-6 py-4 border-b ${borderColor} flex items-center justify-between flex-shrink-0`}>
            <Dialog.Title className={`text-xl font-semibold ${textColor} flex items-center gap-2`}>
              <SparklesIcon className="w-5 h-5" />
              {context.title || 'Add Evidence'}
              {context?.rubricItemId && (
                <span className={`ml-auto text-xs font-normal px-2 py-1 rounded-full ${
                  darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                }`}>
                  Review Prep
                </span>
              )}
            </Dialog.Title>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tab Navigation - Only show when in review prep context */}
          {context?.rubricItemId && (
            <div className={`px-6 pt-4 flex gap-2 flex-shrink-0`}>
              <button
                onClick={() => setActiveTabMode('create')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTabMode === 'create'
                    ? darkMode 
                      ? 'bg-gray-700 text-white' 
                      : 'bg-gray-200 text-gray-900'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Create New Evidence
              </button>
              <button
                onClick={() => setActiveTabMode('link')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTabMode === 'link'
                    ? darkMode 
                      ? 'bg-gray-700 text-white' 
                      : 'bg-gray-200 text-gray-900'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Link Existing Work
              </button>
            </div>
          )}
          
          {/* Content - Scrollable */}
          <div className="p-6 space-y-4 flex-1 overflow-y-auto">
            {/* Create New Tab Content */}
            {activeTabMode === 'create' ? (
              <>
                {/* Review Prep Context */}
                {context?.rubricItemId && activeRubric && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} border`}>
                {(() => {
                  // First check if we have the rubric details directly from context
                  if (context.rubricCategory && context.rubricDescription) {
                    return (
                      <>
                        <h3 className={`text-sm font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-700'} mb-1`}>
                          Adding Evidence for:
                        </h3>
                        <p className={`text-sm ${darkMode ? 'text-purple-200' : 'text-purple-600'}`}>
                          <span className="font-medium">{context.rubricCategory}:</span> {context.rubricDescription}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-purple-300/70' : 'text-purple-500'} mt-2`}>
                          💡 Think about specific examples where you demonstrated this skill
                        </p>
                      </>
                    );
                  }
                  
                  // Otherwise, try to find the rubric item details from the activeRubric
                  let rubricDetails = null;
                  
                  activeRubric.categories.forEach(category => {
                    // Check new format with criteria_items
                    if (category.criteria_items && category.criteria_items.length > 0) {
                      const item = category.criteria_items.find(c => c.id === context.rubricItemId);
                      if (item) {
                        rubricDetails = {
                          category: category.name,
                          text: item.text || item.description || ''
                        };
                      }
                    }
                    
                    // Also check if rubricItemId might be in the old format
                    if (!rubricDetails && category.criteria && Array.isArray(category.criteria)) {
                      // Check if this is a legacy format ID like "category-index"
                      const parts = context.rubricItemId.split('-');
                      const itemIndex = parseInt(parts[parts.length - 1]);
                      const categoryMatch = parts.slice(0, -1).join('-');
                      
                      if (!isNaN(itemIndex) && category.criteria[itemIndex]) {
                        // Check if category matches
                        const categorySlug = category.name.toLowerCase().replace(/\s+/g, '-');
                        if (categoryMatch === categorySlug || categoryMatch === category.id) {
                          rubricDetails = {
                            category: category.name,
                            text: category.criteria[itemIndex]
                          };
                        }
                      }
                    }
                  });
                  
                  if (rubricDetails) {
                    return (
                      <>
                        <h3 className={`text-sm font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-700'} mb-1`}>
                          Adding Evidence for:
                        </h3>
                        <p className={`text-sm ${darkMode ? 'text-purple-200' : 'text-purple-600'}`}>
                          <span className="font-medium">{rubricDetails.category}:</span> {rubricDetails.text}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-purple-300/70' : 'text-purple-500'} mt-2`}>
                          💡 Think about specific examples where you demonstrated this skill
                        </p>
                      </>
                    );
                  }
                  
                  // Fallback message if we can't find the rubric item
                  return (
                    <>
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-700'} mb-1`}>
                        Adding Evidence for Rubric Criterion
                      </h3>
                      <p className={`text-xs ${darkMode ? 'text-purple-300/70' : 'text-purple-500'}`}>
                        💡 Document specific examples that demonstrate your skills and achievements
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* Title */}
            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Title <span className="text-red-500">*</span>
              </label>
              <input
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={context?.rubricItemId ? "What specific task or situation demonstrates this skill?" : "What did you accomplish?"}
                className={`
                  w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBgColor} ${textColor}
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                `}
              />
            </div>

            {/* Content */}
            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Details
                {context?.rubricItemId && (
                  <span className={`ml-2 text-xs font-normal ${mutedTextColor}`}>
                    (Include: What you did, How you did it, What was the outcome)
                  </span>
                )}
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={context?.rubricItemId 
                  ? "Describe: 1) The situation/challenge, 2) Your specific actions, 3) The positive outcome or impact"
                  : context.placeholder || "Describe your experience..."}
                rows={4}
                className={`
                  w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBgColor} ${textColor}
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                `}
              />
            </div>

            {/* Quick Prompts for Review Prep */}
            {context?.rubricItemId && !title && !content && (
              <div className={`text-xs ${mutedTextColor} space-y-1`}>
                <p className="font-medium">Need help remembering? Consider:</p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>A challenging bug you fixed</li>
                  <li>A feature you implemented</li>
                  <li>Feedback you received from teammates</li>
                  <li>A process you improved</li>
                  <li>Knowledge you shared with others</li>
                </ul>
              </div>
            )}

            {/* Impact Metrics */}
            <div className="space-y-4">
              {/* Career Impact */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${textColor}`}>
                    Career Impact
                  </label>
                  <span className={`text-sm ${mutedTextColor}`}>
                    {IMPACT_LEVELS[careerImpact as keyof typeof IMPACT_LEVELS]?.label}
                  </span>
                </div>
                {isMobile ? (
                  <select
                    value={careerImpact}
                    onChange={(e) => setCareerImpact(parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded ${inputBgColor} ${borderColor} ${textColor}`}
                  >
                    {Object.entries(IMPACT_LEVELS).map(([value, { label }]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                ) : (
                  <Slider
                    value={careerImpact}
                    min={1}
                    max={5}
                    step={1}
                    marks
                    onChange={(_, newValue) => setCareerImpact(newValue as number)}
                    sx={{
                      '& .MuiSlider-track': {
                        backgroundColor: CAREER_IMPACT_COLORS.primary,
                      },
                      '& .MuiSlider-thumb': {
                        backgroundColor: CAREER_IMPACT_COLORS.primary,
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0 0 0 8px ${CAREER_IMPACT_COLORS.hover}`,
                        },
                      },
                      '& .MuiSlider-mark': {
                        backgroundColor: '#94a3b8',
                        '&.MuiSlider-markActive': {
                          backgroundColor: CAREER_IMPACT_COLORS.primary,
                        },
                      },
                    }}
                  />
                )}
              </div>

              {/* Engagement Level */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${textColor}`}>
                    Engagement Level
                  </label>
                  <span className={`text-sm ${mutedTextColor}`}>
                    {ENGAGEMENT_LEVELS[engagementLevel as keyof typeof ENGAGEMENT_LEVELS]?.label}
                  </span>
                </div>
                {isMobile ? (
                  <select
                    value={engagementLevel}
                    onChange={(e) => setEngagementLevel(parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded ${inputBgColor} ${borderColor} ${textColor}`}
                  >
                    {Object.entries(ENGAGEMENT_LEVELS).map(([value, { label }]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                ) : (
                  <Slider
                    value={engagementLevel}
                    min={1}
                    max={5}
                    step={1}
                    marks
                    onChange={(_, newValue) => setEngagementLevel(newValue as number)}
                    sx={{
                      '& .MuiSlider-track': {
                        backgroundColor: ENGAGEMENT_COLORS.primary,
                      },
                      '& .MuiSlider-thumb': {
                        backgroundColor: ENGAGEMENT_COLORS.primary,
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0 0 0 8px ${ENGAGEMENT_COLORS.hover}`,
                        },
                      },
                      '& .MuiSlider-mark': {
                        backgroundColor: '#94a3b8',
                        '&.MuiSlider-markActive': {
                          backgroundColor: ENGAGEMENT_COLORS.primary,
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>

            {/* Link to Goals and Rubric */}
            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                {isPerformanceReview ? 'Link to Goals & Competencies' : 'Link to Goals & Evaluation Criteria'}
                {context?.rubricItemId && selectedItems.size > 0 && (
                  <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                  </span>
                )}
              </label>
              {linkedItems.length > 0 ? (
                <div className={`max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2 ${borderColor}`}>
                  {linkedItems.map((item) => (
                    <label
                      key={`${item.type}-${item.id}`}
                      className={`
                        flex items-center gap-3 p-2 rounded-lg cursor-pointer
                        hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={selectedItems.has(`${item.type}-${item.id}`)}
                        onChange={() => toggleItem(`${item.type}-${item.id}`)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1 flex items-center justify-between">
                        <span className={`text-sm ${textColor}`}>{item.name}</span>
                        {item.type === 'goal' && item.mappedCount && item.mappedCount > 0 && (
                          <span className={`text-xs ${mutedTextColor} ml-2`}>
                            ({item.mappedCount} criteria)
                          </span>
                        )}
                        {item.type === 'competency' && item.weight && (
                          <span className={`text-xs ${mutedTextColor} ml-2`}>
                            {item.weight}%
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div className={`text-sm ${mutedTextColor} p-4 border rounded-lg ${borderColor} text-center`}>
                  {isPerformanceReview 
                    ? 'No competencies set up yet. Complete your performance review setup to define competencies.'
                    : 'No goals or evaluation criteria set up yet. Add them in the Goals & Rubric tabs.'
                  }
                </div>
              )}
            </div>
              </>
            ) : (
              /* Link Existing Tab Content */
              <LinkExistingContent
                darkMode={darkMode}
                focus={focus}
                rubricItemId={context?.rubricItemId}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedExistingItems={selectedExistingItems}
                setSelectedExistingItems={setSelectedExistingItems}
              />
            )}
          </div>
          
          {/* Footer - Fixed */}
          <div className={`px-6 py-4 border-t ${borderColor} flex items-center justify-between flex-shrink-0`}>
            <div className={`text-sm ${mutedTextColor}`}>
              {activeTabMode === 'create' && selectedItems.size > 0 && (
                <span>
                  Linking to {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''}
                </span>
              )}
              {activeTabMode === 'link' && selectedExistingItems.size > 0 && (
                <span>
                  {selectedExistingItems.size} item{selectedExistingItems.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg border ${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  activeTabMode === 'create' 
                    ? !title.trim() || isSubmitting
                    : selectedExistingItems.size === 0 || isSubmitting
                }
                className={`
                  px-4 py-2 rounded-lg font-medium transition-all
                  ${(activeTabMode === 'create' ? title.trim() : selectedExistingItems.size > 0) && !isSubmitting
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {isSubmitting 
                  ? activeTabMode === 'create' ? 'Adding...' : 'Linking...'
                  : activeTabMode === 'create' ? 'Add Evidence' : 'Link Selected'
                }
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

// Link Existing Content Component
interface LinkExistingContentProps {
  darkMode: boolean;
  focus: any;
  rubricItemId?: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedExistingItems: Set<string>;
  setSelectedExistingItems: (items: Set<string>) => void;
}

const LinkExistingContent: React.FC<LinkExistingContentProps> = ({
  darkMode,
  focus,
  rubricItemId,
  searchQuery,
  setSearchQuery,
  selectedExistingItems,
  setSelectedExistingItems
}) => {
  const { cards, isLoading: cardsLoading } = useCareerCards({ focusId: focus?.id });
  const { notes, loading: notesLoading } = useNotes();
  const { getFilteredLinks } = useContentLinks();
  const [existingLinks, setExistingLinks] = useState<Set<string>>(new Set());
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-200' : 'text-gray-800';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBgColor = darkMode ? 'bg-gray-700' : 'bg-white';
  
  // Fetch existing links for this rubric item
  useEffect(() => {
    const fetchExistingLinks = async () => {
      if (!rubricItemId) return;
      
      const links = await getFilteredLinks({
        targetType: 'rubric_criterion',
        targetId: rubricItemId
      });
      
      const linkedIds = new Set(links.map(link => `${link.source_type}-${link.source_id}`));
      setExistingLinks(linkedIds);
    };
    
    fetchExistingLinks();
  }, [rubricItemId, getFilteredLinks]);
  
  // Filter and combine cards and notes
  const allItems = [
    ...(cards || []).map(card => ({
      id: card.id,
      type: 'career_card' as const,
      title: card.title || card.card?.card_data?.title || 'Untitled Card',
      content: card.card?.card_data?.overview || card.card?.card_data?.summary || '',
      date: card.created_at || card.card?.created_at,
      cardType: card.card?.card_data?.type || 'Unknown',
      isLinked: existingLinks.has(`career_card-${card.id}`)
    })),
    ...(notes || []).map(note => ({
      id: note.id,
      type: 'note' as const,
      title: note.title || 'Untitled Note',
      content: note.content || '',
      date: note.created_at,
      cardType: 'Note',
      isLinked: existingLinks.has(`note-${note.id}`)
    }))
  ]
    .filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return item.title.toLowerCase().includes(query) || 
             item.content.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      // Sort by: linked first, then by date
      if (a.isLinked && !b.isLinked) return -1;
      if (!a.isLinked && b.isLinked) return 1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  
  const toggleItem = (itemKey: string) => {
    const newItems = new Set(selectedExistingItems);
    if (newItems.has(itemKey)) {
      newItems.delete(itemKey);
    } else {
      newItems.add(itemKey);
    }
    setSelectedExistingItems(newItems);
  };
  
  const loading = cardsLoading || notesLoading;
  
  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${mutedTextColor}`} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search your career cards and notes..."
          className={`
            w-full pl-10 pr-3 py-2 rounded-lg border ${borderColor} ${inputBgColor} ${textColor}
            focus:outline-none focus:ring-2 focus:ring-blue-500
          `}
        />
      </div>
      
      {/* Results */}
      <div className={`max-h-80 overflow-y-auto space-y-2 ${loading ? 'opacity-50' : ''}`}>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className={`mt-2 text-sm ${mutedTextColor}`}>Loading your content...</p>
          </div>
        ) : allItems.length === 0 ? (
          <div className={`text-center py-8 ${mutedTextColor}`}>
            <p>No career cards or notes found.</p>
            <p className="text-sm mt-1">Create some evidence first!</p>
          </div>
        ) : (
          allItems.map(item => (
            <label
              key={`${item.type}-${item.id}`}
              className={`
                block p-3 rounded-lg cursor-pointer border transition-all
                ${selectedExistingItems.has(`${item.type}-${item.id}`)
                  ? darkMode 
                    ? 'bg-blue-900/30 border-blue-600' 
                    : 'bg-blue-50 border-blue-400'
                  : `${bgColor} ${borderColor} hover:border-gray-400`
                }
                ${item.isLinked ? 'opacity-60' : ''}
              `}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selectedExistingItems.has(`${item.type}-${item.id}`)}
                  onChange={() => toggleItem(`${item.type}-${item.id}`)}
                  className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  disabled={item.isLinked}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.type === 'career_card'
                        ? darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                        : darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      {item.cardType}
                    </span>
                    {item.isLinked && (
                      <span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                        ✓ Already linked
                      </span>
                    )}
                  </div>
                  <h4 className={`font-medium ${textColor} truncate`}>{item.title}</h4>
                  {item.content && (
                    <p className={`text-sm ${mutedTextColor} line-clamp-2 mt-1`}>
                      {item.content}
                    </p>
                  )}
                  <p className={`text-xs ${mutedTextColor} mt-1`}>
                    {new Date(item.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </label>
          ))
        )}
      </div>
      
      {/* Selection Summary */}
      {selectedExistingItems.size > 0 && (
        <div className={`text-sm ${mutedTextColor} text-center`}>
          {selectedExistingItems.size} item{selectedExistingItems.size !== 1 ? 's' : ''} selected to link
        </div>
      )}
    </div>
  );
};

export default EnhancedQuickAddModal;